import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { toast } from 'react-toastify'
import { redirect, PRIVATE_PATHS } from 'utils/paths'
import { getEstate, detachBp } from 'services/estateService'
import { deleteEstateRoutine } from 'ducks/estates/actions'
import { cardStyle, font, ink, line, radius, surface, transition, labelStyle, bp as bpToken } from 'features/dashboard/tokens'
import { RAG, scoreColour, scoreBand, formatPercent } from 'features/dashboard/charts/scoreColour'
import EstateEditorModal from 'features/bpList/Components/estates/EstateEditorModal'
import ConfirmModal from 'features/portfolio/components/ConfirmModal'
import AccessManager from 'features/accessControl/AccessManager'
import EstateDocumentsTab from 'features/bpList/Components/estates/EstateDocumentsTab'
import GuruChat from 'features/bpDetails/Components/guru/GuruChat'
import DetailShell, { useDetailTab } from 'components/DetailShell'
import { useAccessCount } from 'features/accessControl/hooks/useAccessCount'
import PassportHeader, { PassportAction, PassportDangerAction, PassportOverviewIntro } from 'components/PassportHeader'
import CompliancePulse from 'features/bpDetails/Components/CompliancePulse'
import PassportsMap from 'features/portfolios/components/PassportsMap'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import BuildingPassportList from 'components/BuildingPassportList'
import FeatureSwitchBar from 'components/FeatureSwitchBar'
import ActivityLog from 'components/ActivityLog'
import RemedialsWorkspace from 'features/actions/workspace/RemedialsWorkspace'
import IncidentsPage from 'features/incidents/IncidentsPage'
import PortfolioTasksTab from 'features/portfolio/components/PortfolioTasksTab'
import { selectComplianceEnabled, selectFeatureVisibility, selectFeatureDefaultOwner } from 'features/auth/ducks/selectors'
import { isFeatureVisible } from 'constants/featureGroups'

const scoreTitle = score =>
  score === null || score === undefined
    ? 'No compliance score yet. The share of weighted obligations satisfied could not be calculated.'
    : `Compliance score ${formatPercent(score)} (${scoreBand(score)}). The share of weighted obligations satisfied, where each obligation is weighted by its criticality.`

const EstateDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const complianceEnabled = useSelector(selectComplianceEnabled)
  const [estate, setEstate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [tab, setTab] = useDetailTab((estate?.compliance?.buildings || estate?.buildingPassports || []).length > 0 ? 'guru' : 'overview')
  const [removeBpTarget, setRemoveBpTarget] = useState(null)
  const [deleteEstateOpen, setDeleteEstateOpen] = useState(false)
  const currentUserId = useSelector(state => state.auth?.user?.id ?? null)
  const [accessCount, updateAccessCount] = useAccessCount('estate', id, !!estate, currentUserId)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getEstate(id)
      setEstate(res?.data?.data || null)
    } catch (e) {
      toast.error('Could not load this estate.')
      setEstate(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])


  const handleRemoveBuilding = useCallback(bpId => {
    const building = (estate?.buildingPassports || []).find(b => b.id === bpId)
    const label = building?.name || 'this Building Passport'
    setRemoveBpTarget({ bpId, label })
  }, [estate])

  const confirmRemoveBuilding = useCallback(async () => {
    if (!removeBpTarget) return
    const { bpId, label } = removeBpTarget
    setRemoveBpTarget(null)
    try {
      await detachBp({ id, bpId })
      toast.success(`${label} removed from this Estate Passport.`)
      load()
    } catch (e) {
      toast.error('Could not remove that Building Passport. Please try again.')
    }
  }, [removeBpTarget, id, load])

  const handleDelete = () => setDeleteEstateOpen(true)

  const confirmDeleteEstate = () => {
    setDeleteEstateOpen(false)
    dispatch(deleteEstateRoutine({ id }))
    navigate('/bp-list?tab=estate')
  }

  const buildings = (() => {
    const source = estate?.buildingPassports || []
    const byId = new Map(source.map(b => [b.id, b]))
    const scored = estate?.compliance?.buildings
    if (!scored) {
      return source.map(b => ({ ...b, score: null, unmetStatutory: 0, total: 0, satisfied: 0, expired: 0, pending: 0 }))
    }
    return scored.map(b => ({ ...(byId.get(b.id) || {}), ...b }))
  })()
  const attention = buildings
    .filter(b => (b.unmetStatutory || 0) > 0 || (b.expired || 0) > 0)
    .sort((a, b) => (b.unmetStatutory || 0) - (a.unmetStatutory || 0) || (a.score ?? 101) - (b.score ?? 101))
  const leadName = estate?.lead
    ? [estate.lead.firstName, estate.lead.lastName].filter(Boolean).join(' ')
    : null
  const memberBps = estate?.buildingPassports || []
  const anyBpHasTaskManagement = memberBps.some(b => b.taskManagementEnabled || b.hasTaskManagement || b.has_task_management)
  const featureVisibility = useSelector(selectFeatureVisibility)
  const featureOwner = useSelector(selectFeatureDefaultOwner)
  const tasksVisible = isFeatureVisible(featureVisibility, 'tasks', featureOwner)
  const incidentsVisible = isFeatureVisible(featureVisibility, 'incidents', featureOwner)
  const tmHint = anyBpHasTaskManagement
    ? null
    : 'Requires Task Management on at least one Building Passport in this estate.'
  const hasBuildings = buildings.length > 0
  const guruHint = hasBuildings
    ? null
    : 'Requires at least one Building Passport in this estate. Use Edit estate to add one.'
  // A hint that explains why something is locked always wins over the plain
  // description of what the section is.
  const sectionHint = (locked, reason, description) => (locked ? reason : description)

  const tabs = [
    { key: 'overview', label: 'Overview', icon: DashboardOutlinedIcon, hint: `This estate at a glance: its ${buildings.length} Building Passport${buildings.length === 1 ? '' : 's'} and how they are doing.` },
    { key: 'tasks', label: 'Tasks', icon: AssignmentOutlinedIcon, disabled: !anyBpHasTaskManagement, hint: sectionHint(!anyBpHasTaskManagement, tmHint, 'Scheduled work across every building in this estate.') },
    { key: 'actions', label: 'Remedials', icon: ChecklistOutlinedIcon, disabled: !anyBpHasTaskManagement, hint: sectionHint(!anyBpHasTaskManagement, tmHint, 'Problems found that still need putting right, across the estate.') },
    { key: 'incidents', label: 'Incidents', icon: ReportProblemOutlinedIcon, disabled: !anyBpHasTaskManagement, hint: sectionHint(!anyBpHasTaskManagement, tmHint, 'Reported incidents and near misses across the estate.') },
    { key: 'documents', label: 'Documents', icon: FolderOutlinedIcon, hint: 'Documents held for this estate and the buildings in it.' },
    { key: 'guru', label: 'Guru Chat', icon: AutoAwesomeOutlinedIcon, disabled: !hasBuildings, hint: sectionHint(!hasBuildings, guruHint, 'Ask questions about any document across this estate.') },
    { key: 'access', label: 'Access', icon: LockPersonOutlinedIcon, count: accessCount ? accessCount.toLocaleString('en-GB') : undefined, hint: accessCount ? `${accessCount.toLocaleString('en-GB')} ${accessCount === 1 ? 'person has' : 'people have'} access to this estate. See who they are and what they are allowed to do.` : 'Who can see this estate, and what they are allowed to do.' },
    { key: 'activity', label: 'Activity', icon: HistoryOutlinedIcon, hint: 'A plain-language record of what has happened in this estate.' },
  ].filter(t => {
    if (t.key === 'tasks' || t.key === 'actions') return tasksVisible
    if (t.key === 'incidents') return incidentsVisible
    return true
  })

  if (loading) {
    return (
      <DetailShell
        backLabel='All estates'
        onBack={() => navigate('/bp-list?tab=estate')}
        loading
      />
    )
  }

  if (!estate) {
    return (
      <DetailShell
        backLabel='All estates'
        onBack={() => navigate('/bp-list?tab=estate')}
        notFound
      />
    )
  }

  const showCompliance = complianceEnabled && anyBpHasTaskManagement
  const createdOn = estate.createdAt
    ? new Date(estate.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null

  return (
    <DetailShell
      backLabel='All estates'
      onBack={() => navigate('/bp-list?tab=estate')}
      headerCard={(
        <PassportHeader
          kind='estate'
          name={estate.name}
          subtitle={estate.portfolio ? `In the ${estate.portfolio.name} portfolio` : undefined}
          toolbar={<FeatureSwitchBar surface='passport' isOwner={featureOwner} compact />}
          actions={(
            <>
              {showCompliance && (
                <PassportAction
                  as='button'
                  type='button'
                  onClick={() => navigate(`${PRIVATE_PATHS.dashboard}?altitude=estate&entityId=${id}`)}
                  title='See this estate on the compliance dashboard'
                >
                  <SpaceDashboardOutlinedIcon style={{ fontSize: 16 }} /> Dashboard
                </PassportAction>
              )}
              {estate.canManage && (
                <PassportAction as='button' type='button' onClick={() => setEditing(true)} title='Add Building Passports to this estate'>
                  <AddRoundedIcon style={{ fontSize: 16 }} /> Add Building Passports
                </PassportAction>
              )}
              {estate.canEdit && (
                <PassportAction as='button' type='button' onClick={() => setEditing(true)} title='Rename this estate or change its lead'>
                  <EditOutlinedIcon style={{ fontSize: 16 }} /> Edit estate
                </PassportAction>
              )}
              {estate.canManage && (
                <PassportDangerAction as='button' type='button' onClick={handleDelete} title='Delete this estate'>
                  <DeleteOutlineIcon style={{ fontSize: 16 }} /> Delete
                </PassportDangerAction>
              )}
            </>
          )}
          complianceScope={showCompliance ? { estateId: id } : null}
          onOpenCompliance={() => setTab('overview')}
        />
      )}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === 'access' && <AccessManager scopeType='estate' scopeId={id} scopeName={estate.name} canManage={!!estate.canManage} isOwner={!!estate.canManage} onUsersChanged={updateAccessCount} />}
      {tab === 'activity' && <ActivityLog targetType='estate' targetId={id} />}
      {tab === 'tasks' && anyBpHasTaskManagement && <PortfolioTasksTab estateId={id} />}
      {tab === 'actions' && anyBpHasTaskManagement && <RemedialsWorkspace scope={{ kind: 'estate', id }} scopedBps={memberBps} />}
      {tab === 'incidents' && anyBpHasTaskManagement && <IncidentsPage estateId={id} availableBPs={memberBps} />}
      {tab === 'documents' && <EstateDocumentsTab estate={estate} buildings={buildings} />}
      {tab === 'guru' && hasBuildings && (
        <ChatContainer>
          <GuruChat estateId={estate.id} estateName={estate.name} buildingCount={buildings?.length ?? 0} buildings={buildings} />
        </ChatContainer>
      )}

      {tab === 'overview' && (
        <OverviewStack>
          <PassportOverviewIntro
            highlight={anyBpHasTaskManagement ? (
              <CompliancePulse
                compact
                estateId={id}
                showCompliance={complianceEnabled}
                showTasks={tasksVisible}
                showIncidents={incidentsVisible}
              />
            ) : null}
            fields={[
              { label: 'Lead', icon: PersonOutlineOutlinedIcon, value: leadName || 'Not set' },
              {
                label: 'Portfolio',
                emblem: 'portfolio',
                emblemTone: estate.portfolio ? 'solid' : 'soft',
                value: estate.portfolio ? estate.portfolio.name : 'Not in a portfolio',
                onClick: estate.portfolio ? () => redirect(`portfolios/${estate.portfolio.id}`) : undefined,
                title: estate.portfolio ? 'Open this portfolio' : undefined
              },
              createdOn ? { label: 'Created', icon: EventOutlinedIcon, value: createdOn } : null,
              { label: 'Description', icon: NotesOutlinedIcon, value: estate.description, wrap: true },
            ]}
          />
          {buildings.length > 0 && (
            <PassportsMap
              points={buildings.map(b => ({ id: b.id, name: b.name, postcode: b.postalCode, score: b.score }))}
              onOpenBp={bpId => redirect(`bp-list/${bpId}`)}
              showCompliance={showCompliance}
              fitToPoints
            />
          )}
            {complianceEnabled && anyBpHasTaskManagement && attention.length > 0 && (
              <AttentionBlock>
                <Label>Needs attention</Label>
                <AttentionList>
                  {attention.map(building => (
                    <AttentionRow
                      key={building.id}
                      type='button'
                      onClick={() => redirect(`bp-list/${building.id}/programme`)}
                    >
                      <AttentionName title={building.name}>{building.name}</AttentionName>
                      {building.unmetStatutory > 0 && (
                        <Warning>⚠ {building.unmetStatutory} unmet statutory</Warning>
                      )}
                      {building.expired > 0 && (
                        <ExpiredNote>{building.expired} expired</ExpiredNote>
                      )}
                      <AttentionCta>Open compliance tracker →</AttentionCta>
                    </AttentionRow>
                  ))}
                </AttentionList>
              </AttentionBlock>
            )}
          <Toolbar>
            <Summary>
              <strong>{buildings.length}</strong> Building Passport{buildings.length === 1 ? '' : 's'} in this Estate Passport
            </Summary>
            {estate.canManage && (
              <ToolbarActions>
                <AddBpButton type='button' onClick={() => setEditing(true)}>
                  + Add Building Passports
                </AddBpButton>
              </ToolbarActions>
            )}
          </Toolbar>

          {buildings.length === 0 ? (
            <Note>
              No Building Passports in this estate yet.
              {estate.canManage ? ' Use “Add Building Passports” above to put some in.' : ''}
            </Note>
          ) : (
            <BuildingPassportList
              buildings={buildings}
              inset={false}
              onRemove={estate.canManage ? handleRemoveBuilding : undefined}
              removeLabel='Remove from this Estate Passport'
              renderLead={complianceEnabled && anyBpHasTaskManagement ? (b => (
                <BuildingRing
                  $colour={scoreColour(b.score)}
                  $deg={(b.score ?? 0) * 3.6}
                  title={scoreTitle(b.score)}
                  aria-label={scoreTitle(b.score)}
                >
                  <BuildingRingInner>{formatPercent(b.score)}</BuildingRingInner>
                </BuildingRing>
              )) : undefined}
              renderMeta={complianceEnabled && anyBpHasTaskManagement ? (b => (
                <>
                  {b.unmetStatutory > 0
                    ? <Warning>⚠ {b.unmetStatutory} unmet statutory</Warning>
                    : <Calm>No statutory gaps</Calm>}
                  <MetaDim>{b.satisfied || 0} satisfied · {b.expired || 0} expired · {b.pending || 0} pending</MetaDim>
                </>
              )) : undefined}
              links={b => [
                { label: 'Overview', onClick: () => redirect(`bp-list/${b.id}`) },
                ...(complianceEnabled && anyBpHasTaskManagement
                  ? [{ label: 'Compliance', onClick: () => redirect(`bp-list/${b.id}/programme`) }]
                  : []),
                { label: 'Remedials', onClick: () => redirect(`bp-list/${b.id}/remedials`) },
                { label: 'Documents', onClick: () => redirect(`bp-list/${b.id}/files`) },
              ]}
            />
          )}
        </OverviewStack>
      )}

          {editing && (
            <EstateEditorModal
              open
              estate={estate}
              onClose={() => {
                setEditing(false)
                load()
              }}
            />
          )}

          {removeBpTarget && (
            <ConfirmModal
              title='Remove Building Passport?'
              message={`Remove ${removeBpTarget.label} from this Estate Passport? The Building Passport itself is not deleted. It just leaves the group.`}
              confirmLabel='Remove'
              onConfirm={confirmRemoveBuilding}
              onClose={() => setRemoveBpTarget(null)}
            />
          )}

          {deleteEstateOpen && (
            <ConfirmModal
              title='Delete this estate?'
              message='Its Building Passports are kept. They just leave the group.'
              confirmLabel='Delete estate'
              onConfirm={confirmDeleteEstate}
              onClose={() => setDeleteEstateOpen(false)}
            />
          )}
    </DetailShell>
  )
}

export default EstateDetailPage







const ChatContainer = styled.div`
  ${cardStyle}
  height: calc(100vh - 260px);
  min-height: 500px;
  overflow: hidden;

  @media (max-width: ${bpToken.mobile}) {
    height: calc(100vh - 220px);
    min-height: 400px;
  }
`

const OverviewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const Summary = styled.div`
  font-size: ${font.body};
  color: ${ink.soft};

  strong { color: ${ink.base}; font-weight: 800; }
`

const ToolbarActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`




const Label = styled.span`
  ${labelStyle}
`






const Warning = styled.span`
  font-size: ${font.caption};
  font-weight: 600;
  color: ${RAG.critical.fg};
`

const Calm = styled.span`
  font-size: ${font.caption};
  color: ${ink.mute};
`











const BuildingRing = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: ${radius.round};
  background: ${({ $colour, $deg }) => `conic-gradient(${$colour} ${$deg}deg, ${line.base} 0)`};
  display: flex;
  align-items: center;
  justify-content: center;
`





const AttentionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AttentionRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: ${RAG.critical.fg}08;
  border: 1px solid ${RAG.critical.fg}26;
  border-left: 3px solid ${RAG.critical.fg};
  border-radius: ${radius.sm};
  cursor: pointer;
  transition: ${transition};

  &:hover {
    background: ${RAG.critical.fg}12;
    border-color: ${RAG.critical.fg}40;
  }

  @media (max-width: ${bpToken.mobile}) {
    flex-wrap: wrap;
    gap: 8px 12px;
  }
`

const AttentionName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${font.body};
  font-weight: 600;
  color: ${ink.base};

  @media (max-width: ${bpToken.mobile}) {
    flex: 1 1 100%;
    white-space: normal;
  }
`

const ExpiredNote = styled.span`
  flex-shrink: 0;
  font-size: ${font.caption};
  font-weight: 700;
  color: ${RAG.critical.fg};
`

const AttentionCta = styled.span`
  flex-shrink: 0;
  font-size: ${font.caption};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
`





const Note = styled.div`
  font-size: ${font.body};
  color: ${ink.mute};
`

const AttentionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
`










const AddBpButton = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: ${radius.sm};
  background: #e6f7f9;
  color: #1f8c97;
  font-family: inherit;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover { background: #d5f1f4; }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`

const BuildingRingInner = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${radius.round};
  background: ${surface.card};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${font.caption};
  font-weight: 800;
  color: ${ink.base};
  font-variant-numeric: tabular-nums;
`

const MetaDim = styled.span`
  color: ${ink.mute};
`
